import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { createEditor, Descendant, Text, Editor, Transforms, Element as SlateElement } from 'slate';
import { Slate, Editable, withReact, useSlate, RenderElementProps, RenderLeafProps } from 'slate-react';
import { withHistory } from 'slate-history';
import { theme } from 'antd';

import '@tools/styles/slateEditor.css';
import { EMOJI_CATEGORIES } from '@tools/components/emojis.tsx';
import {
  escapeHtml,
  escapeHtmlAttribute,
  normalizeSafeHref,
  sanitizeRichTextHtml,
} from '@tools/utils/richTextHtml.ts';

export type SlateEditorToolbarMode = 'full' | 'clinical';

interface RichTextEditorProps {
  initialValue?: string;
  className?: string;
  onChange: (value: string) => void;
  value?: string;
  toolbar?: SlateEditorToolbarMode;
  placeholder?: string;
  minHeight?: number;
}

// // Emojis organizados por categorías
// const EMOJI_CATEGORIES = {
//   'Caritas': ['😀', '😃', '😄', '😁', '😆', '🥹', '😅', '😂', '🤣', '🥲', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓'],
//   'Emociones': ['😎', '🥸', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥'],
//   'Gestos': ['😬', '🤐', '🤫', '🤭', '😶', '😐', '😑', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕'],
//   'Animales': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐻‍❄️', '🐨', '🐯', '🦁', '🐮', '🐷', '🐽', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉'],
//   'Comida': ['🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🌽', '🥕', '🧄', '🧅', '🍄', '🥔', '🍠'],
//   'Objetos': ['⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛼', '🛷']
// };

// YouTube URL patterns
const YOUTUBE_PATTERNS = [
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&\s]+)/,
  /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?\s]+)/,
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?\s]+)/,
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([^?\s]+)/
];

const extractYouTubeId = (url: string): string | null => {
  for (const pattern of YOUTUBE_PATTERNS) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const deserialize = (el: Node): Descendant[] => {
  if (el.nodeType === 3) {
    return [{ text: (el.textContent || '').replace(/\n/g, '') }];
  } else if (el.nodeType !== 1) {
    return [];
  }

  const node = el as HTMLElement;
  if (node.nodeName === 'BR') {
    return [{ text: '\n' }];
  }

  const children = Array.from(node.childNodes).flatMap(deserialize);
  if (children.length === 0) {
    children.push({ text: '' });
  }

  switch (node.nodeName) {
    case 'BODY':
      return children;
    case 'P':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [{ type: 'paragraph', children, align: 'left' }] as any;
    case 'H1':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [{ type: 'heading-one', children, align: 'left' }] as any;
    case 'H2':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [{ type: 'heading-two', children, align: 'left' }] as any;
    case 'H3':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [{ type: 'heading-three', children, align: 'left' }] as any;
    case 'UL':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [{ type: 'bulleted-list', children }] as any;
    case 'OL':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [{ type: 'numbered-list', children }] as any;
    case 'LI':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [{ type: 'list-item', children }] as any;
    case 'A':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [{ type: 'link', url: node.getAttribute('href') || '', children }] as any;
    case 'DIV':
      if (node.className.includes('youtube-embed')) {
        const videoId = node.getAttribute('data-video-id');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return [{ type: 'youtube-embed', videoId, children: [{ text: '' }] }] as any;
      }
      return children;
    case 'STRONG':
      return children.map(child => ({ ...child, bold: true }));
    case 'EM':
      return children.map(child => ({ ...child, italic: true }));
    case 'U':
      return children.map(child => ({ ...child, underline: true }));
    default:
      return children;
  }
};

const serialize = (node: Descendant): string => {
  if (Text.isText(node)) {
    let string = escapeHtml(node.text);
    if (node.bold) string = `<strong>${string}</strong>`;
    if (node.italic) string = `<em>${string}</em>`;
    if (node.underline) string = `<u>${string}</u>`;
    return string;
  }

  const children = node.children.map(n => serialize(n)).join('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const element = node as any;

  switch (element.type) {
    case 'paragraph':
      return `<p style="text-align: ${element.align || 'left'}">${children}</p>`;
    case 'heading-one':
      return `<h1 style="text-align: ${element.align || 'left'}">${children}</h1>`;
    case 'heading-two':
      return `<h2 style="text-align: ${element.align || 'left'}">${children}</h2>`;
    case 'heading-three':
      return `<h3 style="text-align: ${element.align || 'left'}">${children}</h3>`;
    case 'bulleted-list':
      return `<ul>${children}</ul>`;
    case 'numbered-list':
      return `<ol>${children}</ol>`;
    case 'list-item':
      return `<li>${children}</li>`;
    case 'link':
      {
        const href = normalizeSafeHref(element.url);
        return href ? `<a href="${escapeHtmlAttribute(href)}">${children}</a>` : children;
      }
    case 'youtube-embed':
      return `<div class="youtube-embed" data-video-id="${element.videoId}">
        <iframe width="560" height="315" src="https://www.youtube.com/embed/${element.videoId}" frameborder="0" allowfullscreen></iframe>
      </div>`;
    default:
      return children;
  }
};

// Componente selector de emojis mejorado
const EmojiPicker: React.FC<{
  onSelect: (emoji: string) => void;
  onClose: () => void;
  isOpen: boolean;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
}> = ({ onSelect, onClose, isOpen, buttonRef }) => {
  const [activeCategory, setActiveCategory] = useState('🙂');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);

  // Calcular posición del picker basado en la posición del botón
  useEffect(() => {
    if (isOpen && buttonRef?.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: buttonRect.bottom + 8, // 8px debajo del botón
        left: buttonRect.left
      });
    }
  }, [isOpen, buttonRef]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node) &&
        buttonRef?.current && !buttonRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    // retrasamos la suscripción un “tick” para que no se cierre al instante
    const id = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) return null;

  const pickerUI = (
    <div
      ref={pickerRef}
      className="emoji-picker"
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: '320px',
        maxHeight: '350px',
        backgroundColor: '#ffffff',
        border: '1px solid #d1d5db',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header con botón de cerrar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f8f9fa'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
          Seleccionar emoji
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '2px',
            lineHeight: 1,
            borderRadius: '4px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#e5e7eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          ×
        </button>
      </div>

      {/* Categorías */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f8f9fa'
      }}>
        {Object.keys(EMOJI_CATEGORIES).map(category => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            style={{
              flex: 1,
              padding: '8px 4px',
              fontSize: '11px',
              backgroundColor: activeCategory === category ? '#3b82f6' : 'transparent',
              color: activeCategory === category ? 'white' : '#374151',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Grid de emojis con scroll vertical */}
      <div style={{
        flex: 1,
        padding: '12px',
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: '4px',
        alignContent: 'start',
        maxHeight: '240px'
      }}>
        {EMOJI_CATEGORIES[activeCategory as keyof typeof EMOJI_CATEGORIES].map((emoji) => (
          <button
            key={emoji}
            onClick={() => {
              onSelect(emoji);
              // No cerramos automáticamente, el usuario debe cerrar manualmente
            }}
            style={{
              width: '32px',
              height: '32px',
              fontSize: '20px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );

  return ReactDOM.createPortal(pickerUI, document.body);
};

const isMarkActive = (editor: Editor, format: string) => {
  const marks = Editor.marks(editor);
  return marks ? Boolean((marks as Record<string, unknown>)[format]) : false;
};

const toggleMark = (editor: Editor, format: string) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

const isBlockActive = (editor: Editor, format: string) => {
  const [match] = Array.from(
    Editor.nodes(editor, {
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        (n as SlateElement).type === format,
    })
  );
  return !!match;
};

const toggleBlock = (editor: Editor, format: string) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes((n as SlateElement).type as string),
    split: true,
  });

  let newProperties: Partial<SlateElement>;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = { align: isActive ? 'left' : format } as Partial<SlateElement>;
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
      align: 'left' // Siempre inicializar con alineación izquierda
    } as Partial<SlateElement>;
  }
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] } as SlateElement;
    Transforms.wrapNodes(editor, block);
  }
};

const insertLink = async (editor: Editor, allowEmbeds = true) => {
  const url = window.prompt('Ingrese la URL');
  if (!url) return;

  // Check if it's a YouTube URL
  const youtubeId = extractYouTubeId(url);
  if (allowEmbeds && youtubeId) {
    const size = await new Promise<string>(resolve => {
      const wrapper = document.createElement('div');
      wrapper.className = 'yt-size-dialog w-100';
      wrapper.innerHTML = `
        <style>
          .yt-size-dialog{
            position:fixed;top:40%;left:50%;transform:translate(-50%,-50%);
            background:#fff;border:1px solid #d1d5db;border-radius:8px;
            padding:16px;box-shadow:0 4px 16px rgba(0,0,0,.15);z-index:3000;
            font-family:system-ui, sans-serif;text-align:center;
            width:100%;max-width: 400px;
          }
          .yt-size-dialog button{
            width: 100%; padding: 1px 5px;
            border-radius:6px;background:#2a7ccf; color: white;cursor:pointer;
          }
          .yt-size-dialog button:hover{background:#b2e2ed; color: blue;border-color:#93c5fd}
        </style>
        <p style="margin-bottom:25px">Tamaño del video</p>
        <div class="row">
          <div class="col-3 px-2"><button data-size="full">Full</button></div>
          <div class="col-3 px-2"><button data-size="grande">Grande</button></div>
          <div class="col-3 px-2"><button data-size="normal">Normal</button></div>
          <div class="col-3 px-2"><button data-size="chico">Chico</button></div>
        </div>
      `;
      document.body.appendChild(wrapper);
      wrapper.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', () => {
          const s = (btn as HTMLElement).dataset.size || 'full';
          resolve(s);
          document.body.removeChild(wrapper);
        });
      });
    });

    const embed: SlateElement = {
      type: 'youtube-embed',
      videoId: youtubeId,
      size,
      children: [{ text: '' }]
    };
    Transforms.insertNodes(editor, embed);
    return;
  }

  // Regular link
  const safeUrl = normalizeSafeHref(url);
  if (!safeUrl) return;

  const { selection } = editor;
  const link: SlateElement = { type: 'link', url: safeUrl, children: [] };

  if (selection) {
    const selectedText = Editor.string(editor, selection);
    if (selectedText) {
      Transforms.wrapNodes(editor, link, { split: true });
    } else {
      Transforms.insertNodes(editor, { ...link, children: [{ text: safeUrl }] });
    }
  } else {
    Transforms.insertNodes(editor, { ...link, children: [{ text: safeUrl }] });
  }
};

// Componente para selector de tipo de bloque
const BlockTypeSelect: React.FC = () => {
  const editor = useSlate();
  const { token } = theme.useToken();

  const getCurrentBlockType = () => {
    const [match] = Array.from(
      Editor.nodes(editor, {
        match: n => !Editor.isEditor(n) && SlateElement.isElement(n),
      })
    );

    if (match) {
      const element = match[0] as SlateElement;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (element as any).type || 'paragraph';
    }
    return 'paragraph';
  };

  return (
    <select
      className="slate-block-select"
      value={getCurrentBlockType()}
      onChange={e => {
        const blockType = e.target.value;
        if (blockType !== getCurrentBlockType()) {
          toggleBlock(editor, blockType);
        }
      }}
      style={{
        cursor: 'pointer',
        minWidth: '100px',
        height: '32px',
        borderColor: token.colorBorder,
        backgroundColor: token.colorBgContainer,
        color: token.colorText,
      }}
    >
      <option value="paragraph">Párrafo</option>
      <option value="heading-one">Título 1</option>
      <option value="heading-two">Título 2</option>
      <option value="heading-three">Título 3</option>
    </select>
  );
};

const FontSizeSelect: React.FC = () => {
  const editor = useSlate();
  const { token } = theme.useToken();

  return (
    <select
      className="slate-font-select"
      onChange={e => {
        const size = e.target.value;
        if (size) {
          Editor.addMark(editor, 'fontSize', size);
        }
      }}
      defaultValue=""
      style={{
        background: token.colorBgContainer,
        color: token.colorText,
        borderColor: token.colorBorder,
        borderRadius: '4px',
        padding: '4px 8px',
        fontSize: '14px',
        cursor: 'pointer',
        minWidth: '100px',
        height: '32px'
      }}
    >
      <option value="" disabled>Tamaño</option>
      <option value="12px">12</option>
      <option value="14px">14</option>
      <option value="16px">16</option>
      <option value="18px">18</option>
      <option value="20px">20</option>
      <option value="24px">24</option>
    </select>
  );
};

const ToolbarButton: React.FC<{
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
}> = ({ active, onClick, children, title, buttonRef }) => {
  const { token } = theme.useToken();
  return (
    <button
      ref={buttonRef}
      type="button"
      className={`slate-toolbar-btn ${active ? 'active' : ''}`}
      onMouseDown={e => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      style={{
        backgroundColor: active ? token.colorPrimary : token.colorBgContainer,
        color: active ? token.colorTextLightSolid : token.colorText,
        borderColor: active ? token.colorPrimary : token.colorBorder,
      }}
    >
      {children}
    </button>
  );
};

const MarkButton: React.FC<{ format: string; icon: string; title: string }> = ({
  format,
  icon,
  title
}) => {
  const editor = useSlate();
  return (
    <ToolbarButton
      active={isMarkActive(editor, format)}
      onClick={() => toggleMark(editor, format)}
      title={title}
    >
      {icon}
    </ToolbarButton>
  );
};

const BlockButton: React.FC<{ format: string; icon: string; title: string }> = ({
  format,
  icon,
  title
}) => {
  const editor = useSlate();
  return (
    <ToolbarButton
      active={isBlockActive(editor, format)}
      onClick={() => toggleBlock(editor, format)}
      title={title}
    >
      {icon}
    </ToolbarButton>
  );
};

const buildEmptyDocument = (): Descendant[] =>
  ([{ type: 'paragraph', children: [{ text: '' }], align: 'left' }] as unknown as Descendant[]);

const normalizeTopLevelNodes = (nodes: Descendant[]): Descendant[] => {
  const normalized = nodes.map((node) => {
    if (Text.isText(node)) {
      return { type: 'paragraph', children: [node], align: 'left' };
    }
    return node;
  });
  return normalized.length > 0 ? (normalized as Descendant[]) : buildEmptyDocument();
};

const parseHtmlToNodes = (html: string | undefined): Descendant[] => {
  if (!html) return buildEmptyDocument();
  const parsed = new DOMParser().parseFromString(html, 'text/html').body;
  const nodes = deserialize(parsed);
  return normalizeTopLevelNodes(nodes);
};

const SlateEditor: React.FC<RichTextEditorProps> = ({
  initialValue,
  value: externalValue,
  onChange,
  className,
  toolbar = 'full',
  placeholder = 'Escribe aquí tu contenido...',
  minHeight,
}) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const { token } = theme.useToken();
  const resolvedInitialHtml = externalValue ?? initialValue ?? '';
  const [value, setValue] = useState<Descendant[]>(() => parseHtmlToNodes(resolvedInitialHtml));
  const lastSerializedRef = useRef<string>(resolvedInitialHtml);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const nextHtml = externalValue ?? initialValue ?? '';
    const currentHtml = editor.children.map((node) => serialize(node)).join('');
    // El Form normaliza el HTML después de cada cambio. Si solo cambió la
    // presentación y el contenido semántico es el mismo, conservar selección.
    if (
      nextHtml === lastSerializedRef.current
      || sanitizeRichTextHtml(nextHtml) === sanitizeRichTextHtml(currentHtml)
    ) {
      lastSerializedRef.current = nextHtml;
      return;
    }

    const nextValue = parseHtmlToNodes(nextHtml);
    lastSerializedRef.current = nextHtml;
    editor.children = nextValue;
    editor.selection = null;
    setValue(nextValue);
    editor.onChange();
  }, [editor, externalValue, initialValue]);

  const handleChange = (val: Descendant[]) => {
    setValue(val);
    const html = val.map(v => serialize(v)).join('');
    lastSerializedRef.current = html;
    onChange(html);
  };

  const handleEmojiSelect = (emoji: string) => {
    Transforms.insertText(editor, emoji);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderElement = useCallback((props: any) => <Element {...props} />, []);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);

  return (
    <div
      className={`slate-editor-container ${className || ''}`}
      style={{
        borderColor: token.colorBorder,
        backgroundColor: token.colorBgContainer,
        color: token.colorText,
      }}
    >
      <Slate editor={editor} initialValue={value} onChange={handleChange}>
        <div
          className="slate-toolbar"
          style={{
            backgroundColor: token.colorFillAlter,
            borderBottomColor: token.colorBorderSecondary,
          }}
        >
          {toolbar === 'full' ? (
            <div className="slate-toolbar-group">
              <BlockTypeSelect />
            </div>
          ) : null}

          <div className="slate-toolbar-group">
            <MarkButton format="bold" icon="B" title="Negrita" />
            <MarkButton format="italic" icon="I" title="Cursiva" />
            <MarkButton format="underline" icon="U" title="Subrayado" />
          </div>

          <div className="slate-toolbar-group">
            <BlockButton format="numbered-list" icon="1." title="Lista numerada" />
            <BlockButton format="bulleted-list" icon="•" title="Lista con viñetas" />
          </div>

          {toolbar === 'full' ? (
            <div className="slate-toolbar-group">
              <BlockButton format="left" icon="⬅" title="Alinear izquierda" />
              <BlockButton format="center" icon="↔" title="Centrar" />
              <BlockButton format="right" icon="➡" title="Alinear derecha" />
            </div>
          ) : null}

          <div className="slate-toolbar-group">
            <ToolbarButton
              onClick={() => insertLink(editor, toolbar === 'full')}
              title={toolbar === 'full' ? 'Insertar enlace o video de YouTube' : 'Insertar enlace'}
            >
              Link
            </ToolbarButton>
            {toolbar === 'full' ? (
              <div style={{ position: 'relative' }}>
                <ToolbarButton
                  buttonRef={emojiButtonRef}
                  onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
                  title="Insertar emoji"
                >
                  😊
                </ToolbarButton>
                <EmojiPicker
                  isOpen={emojiPickerOpen}
                  onSelect={handleEmojiSelect}
                  onClose={() => setEmojiPickerOpen(false)}
                  buttonRef={emojiButtonRef}
                />
              </div>
            ) : null}
          </div>

          {toolbar === 'full' ? (
            <div className="slate-toolbar-group">
              <FontSizeSelect />
            </div>
          ) : null}
        </div>

        <div
          className="slate-editor-content"
          style={{ backgroundColor: token.colorBgContainer, minHeight }}
        >
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            className="slate-editable"
            style={{ color: token.colorText }}
          />
        </div>
      </Slate>
    </div>
  );
};

const Element: React.FC<RenderElementProps> = ({ attributes, children, element }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const el = element as any;

  // const elementStyle = {
  //   textAlign: el.align || 'left'
  // };

  // Estilos específicos para listas
  const listStyles = {
    margin: '0.5em 0',
    paddingLeft: '1rem' // Espacio fijo de 1rem
  };

  const listItemStyles = {
    margin: '0.25em 0',
    lineHeight: '1.5'
  };

  switch (el.type) {
    case 'heading-one':
      return <h1 {...attributes} className="slate-h1" style={{ textAlign: el.align }}>{children}</h1>;
    case 'heading-two':
      return <h2 {...attributes} className="slate-h2" style={{ textAlign: el.align }}>{children}</h2>;
    case 'heading-three':
      return <h3 {...attributes} className="slate-h3" style={{ textAlign: el.align }}>{children}</h3>;
    case 'bulleted-list':
      return <ul {...attributes} className="slate-ul" style={listStyles}>{children}</ul>;
    case 'numbered-list':
      return <ol {...attributes} className="slate-ol" style={listStyles}>{children}</ol>;
    case 'list-item':
      return <li {...attributes} className="slate-li" style={listItemStyles}>{children}</li>;
    case 'link':
      return (
        <a {...attributes} href={el.url} className="slate-link" target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    case 'youtube-embed':
      {
        const widthClass = el.size || 'full';
        return (
          <div {...attributes} className={`slate-youtube-embed ${widthClass}`} contentEditable={false}>
            <div className={`youtube-wrapper ${widthClass}`}>
              <iframe
                src={`https://www.youtube.com/embed/${el.videoId}`}
                frameBorder={0}
                allowFullScreen
                title="YouTube video"
              />
            </div>
            {children}
          </div>
        );
      }
    default:
      return (
        <p {...attributes} style={{ textAlign: el.align }} className="slate-p">
          {children}
        </p>
      );
  }
};

const Leaf: React.FC<RenderLeafProps> = ({ attributes, children, leaf }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const l = leaf as any;

  if (l.bold) {
    children = <strong>{children}</strong>;
  }
  if (l.italic) {
    children = <em>{children}</em>;
  }
  if (l.underline) {
    children = <u>{children}</u>;
  }

  const style: React.CSSProperties = {};
  if (l.fontSize) {
    style.fontSize = l.fontSize;
  }

  return (
    <span {...attributes} style={style}>
      {children}
    </span>
  );
};

export default SlateEditor;
